/**
 * Phase 120 (Plan 120-01, D-120-08) — FlagsProvider + useFlag().
 *
 * Cloned from `modulesContext.tsx` (mounted INSIDE `AuthProvider` so the
 * `authenticate`-gated `GET /api/release-flags` only fires once
 * `isAuthenticated` is known) but FAIL-CLOSED where modules are fail-open:
 * while the query is loading, on error, with no data, for an unauthenticated
 * visitor (external-portal users never authenticate internally, D-120-10), and
 * when no `FlagsProvider` is mounted, every flag is OFF. A release flag guards
 * unverified work, so hiding it by mistake is the safe failure.
 *
 * The `VITE_DEV_USER` dev stub (same check as `modulesContext.tsx`) turns
 * every flag ON so local development sees the feature.
 *
 * This gate is presentation-only; the enforcement boundary for API behaviour
 * is the backend `requireFlag` preHandler.
 */
import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useAuth } from './authContext';
import { useReleaseFlagValues } from '../hooks/useReleaseFlags';

export interface FlagsContextValue {
  isOn(key: string): boolean;
  isLoading: boolean;
  isError: boolean;
}

const FlagsContext = createContext<FlagsContextValue | null>(null);

/** Matches `modulesContext.tsx` / `authContext.tsx` dev-stub check exactly. */
function isDevStub(): boolean {
  return !!(import.meta.env.DEV && import.meta.env.VITE_DEV_USER);
}

const ALL_OFF: FlagsContextValue = {
  isOn: () => false,
  isLoading: false,
  isError: false,
};

export function FlagsProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const devStub = isDevStub();

  const { data, isLoading, isError } = useReleaseFlagValues(isAuthenticated && !devStub);

  const value = useMemo<FlagsContextValue>(() => {
    if (devStub) {
      return { isOn: () => true, isLoading: false, isError: false };
    }
    return {
      // Off for any key that was not delivered, and for every key while there is no data
      // (loading, or the query disabled for an unauthenticated visitor) or the query is in
      // error (a failed refetch keeps the stale `data`, which must not be trusted).
      isOn: (key: string) => (data && !isError ? data[key] === true : false),
      isLoading,
      isError,
    };
  }, [devStub, data, isLoading, isError]);

  return <FlagsContext.Provider value={value}>{children}</FlagsContext.Provider>;
}

/** The flags context, or an all-off fallback when no `FlagsProvider` is mounted (fail-closed). */
export function useReleaseFlagsContext(): FlagsContextValue {
  return useContext(FlagsContext) ?? ALL_OFF;
}

/** Is the release flag `key` on for this user right now? Off until proven on. */
export function useFlag(key: string): boolean {
  return useReleaseFlagsContext().isOn(key);
}
